import React from 'react';
import { Box, Pressable, Text } from '@gluestack-ui/themed';
import { flexRender, Cell } from '@tanstack/react-table';
import { TableRowProps } from '../types';
import { AdminModelMap, AdminObjectName } from '../../DataColumnConfig';

/**
 * TableRow - A single data row within the DataTable.
 *
 * It provides:
 * - Dynamic cell rendering based on column definitions.
 * - Selection state visualization (background and border highlights).
 * - Hover state effects for interactivity.
 * - Precision layout mapping to ensure alignment with TableHeader.
 * - Integration with TanStack Table's cell rendering system.
 *
 * @param {TableRowProps} props - Configuration for the row data, column widths, and display settings.
 * @returns {React.ReactElement} The rendered table row.
 */
const TableRow = <K extends AdminObjectName>({
  row,
  columnWidths,
  tableColumns,
  minColumnWidth,
  maxColumnWidth,
  height,
  singleLine,
}: TableRowProps<K>) => (
  <Pressable
    testID={`row-${(row.original as { id?: string | number }).id || row.id}`}
    flexDirection="row"
    alignItems="center"
    h={height}
    bg={row.getIsSelected() ? '$bgBrandSubtle' : 'transparent'}
    borderLeftWidth={3}
    borderLeftColor={row.getIsSelected() ? '$borderBrand' : 'transparent'}
    $hover={{
      bg: row.getIsSelected() ? '$bgBrandSubtle' : '$bgHover',
      borderLeftColor: row.getIsSelected() ? '$borderBrand' : '$borderLight',
    }}
  >
    {row.getVisibleCells().map((cell: Cell<AdminModelMap[K], unknown>) => {
      const { id } = cell.column;
      const isSelection = id === 'select';
      const columnDef = cell.column.columnDef as unknown as {
        meta?: { isFixed?: boolean; width?: number; align?: string };
        cell: unknown;
      };
      const isFixed = columnDef.meta?.isFixed || isSelection;
      const metaWidth = columnDef.meta?.width;
      const sizeFromState = columnWidths[id];

      const rawSize = isFixed
        ? metaWidth || 32
        : typeof sizeFromState === 'number'
          ? sizeFromState
          : metaWidth || cell.column.getSize?.() || 180;

      const effectiveMin = tableColumns[id]?.type === 'bool' || isSelection ? 32 : minColumnWidth;
      const clamped = Math.min(Math.max(rawSize, effectiveMin), maxColumnWidth);
      const useFixed = isFixed || rawSize < 100;
      const alignCenter = columnDef.meta?.align === 'center';
      const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());

      return (
        <Box
          key={cell.id}
          testID={`cell-${(row.original as { id?: string | number }).id || row.id}-${cell.column.id}`}
          justifyContent={alignCenter ? 'center' : 'flex-start'}
          alignItems={isSelection ? 'center' : undefined}
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            paddingLeft: 8,
            paddingRight: isSelection ? 0 : 8,
            marginRight: isSelection ? 4 : 10,
            width: useFixed ? clamped : undefined,
            flexGrow: useFixed ? 0 : clamped,
            flexBasis: useFixed ? undefined : 0,
            flexShrink: useFixed ? 0 : 1,
          }}
        >
          {typeof rendered === 'string' ? (
            <Text size="xs" isTruncated={singleLine}>
              {rendered}
            </Text>
          ) : (
            rendered
          )}
        </Box>
      );
    })}
  </Pressable>
  );

export default TableRow;
